import React, { useState, useMemo } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import {
  calculateProvenanceScore,
  calculateValuationSummary,
} from '../../utils/budgetCalculations';
import {
  triggerPrint,
  exportDossierJson,
  formatCurrency,
  getProvenanceBandInfo,
  buildValuationDossierData,
} from '../../utils/printFormatter';
import { ValuationDossier } from './ValuationDossier';
import { DossierSummaryTable } from './DossierSummaryTable';
import { DossierPhotoGrid } from './DossierPhotoGrid';
import { TactilePanel } from '../common/TactilePanel';
import { MechanicalButton } from '../common/MechanicalButton';
import {
  ShieldCheck,
  Printer,
  Download,
  Sliders,
  DollarSign,
  FileText,
  Award,
  CheckCircle2,
  Camera,
  Sparkles,
} from 'lucide-react';

export const DossierView: React.FC = () => {
  const { state } = useRestoMod();
  const {
    project,
    bomItems,
    subletExpenses,
    laborRecords,
    photoLogs,
    subAssemblies,
    hardwareBags,
    tasks,
  } = state;

  // View state: 'workspace' (interactive dashboard) | 'preview' (formal print document)
  const [activeMode, setActiveMode] = useState<'workspace' | 'preview'>('workspace');

  // Editable underwriter declaration settings
  const [preparedBy, setPreparedBy] = useState<string>(
    'RestoMod Studio Appraisals & Provenance'
  );
  const [intendedUnderwriter, setIntendedUnderwriter] = useState<
    'Hagerty' | 'Grundy' | 'American Collectors' | 'Leland-West' | 'Auction Record' | 'General'
  >('Hagerty');
  const [storageFacilityType, setStorageFacilityType] = useState<
    'Climate-Controlled Garage' | 'Locked Residential Garage' | 'Commercial Facility'
  >('Climate-Controlled Garage');
  const [annualEstimatedMileage, setAnnualEstimatedMileage] = useState<number>(2500);
  const [appreciationBufferPct, setAppreciationBufferPct] = useState<number>(12);
  const [customHourlyLaborRate, setCustomHourlyLaborRate] = useState<number>(85);
  const [ownerSignatureDate, setOwnerSignatureDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );

  // Dynamic labor records adjusted with custom labor rate
  const adjustedLaborRecords = useMemo(() => {
    return laborRecords.map((r) => ({
      ...r,
      hourlyRate: customHourlyLaborRate,
    }));
  }, [laborRecords, customHourlyLaborRate]);

  // Provenance Completeness Score calculation (0-100)
  const provenanceScore = useMemo(() => {
    return calculateProvenanceScore({
      vehicle: project,
      photos: photoLogs,
      bomItems,
      subAssemblies,
      hardwareBags,
      subletExpenses,
    });
  }, [project, photoLogs, bomItems, subAssemblies, hardwareBags, subletExpenses]);

  // Agreed-Value Valuation summary calculation
  const valuation = useMemo(() => {
    return calculateValuationSummary({
      vehicle: project,
      bomItems,
      subletExpenses,
      laborRecords: adjustedLaborRecords,
      provenanceScore,
      appreciationBufferPct,
    });
  }, [
    project,
    bomItems,
    subletExpenses,
    adjustedLaborRecords,
    provenanceScore,
    appreciationBufferPct,
  ]);

  const scoreInfo = getProvenanceBandInfo(provenanceScore.totalScore);

  // Assembled ValuationDossier payload
  const dossierPayload = useMemo(() => {
    return buildValuationDossierData({
      vehicle: project,
      bomItems,
      subletExpenses,
      laborRecords: adjustedLaborRecords,
      photoLogs,
      subAssemblies,
      hardwareBags,
      tasks,
      appreciationBufferPct,
      declarations: {
        preparedBy,
        intendedUnderwriter,
        storageFacilityType,
        annualEstimatedMileage,
        ownerSignatureDate,
      },
    });
  }, [
    project,
    bomItems,
    subletExpenses,
    adjustedLaborRecords,
    photoLogs,
    subAssemblies,
    hardwareBags,
    tasks,
    appreciationBufferPct,
    preparedBy,
    intendedUnderwriter,
    storageFacilityType,
    annualEstimatedMileage,
    ownerSignatureDate,
  ]);

  const handlePrint = () => {
    triggerPrint();
  };

  const handleExportJson = () => {
    exportDossierJson(dossierPayload);
  };

  return (
    <div className="space-y-6">
      {/* 1. Master Control Header */}
      <TactilePanel
        title="Collector Vehicle Agreed-Value Valuation Dossier Engine"
        subtitle="Hagerty / Grundy insurance underwriting valuation generator with quantitative provenance scoring"
        badge={
          <span className="p-1.5 bg-shop-darkest rounded border border-shop-border text-safety-amber">
            <ShieldCheck className="w-4 h-4" />
          </span>
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            {/* View Mode Switcher (min-h-[44px], min-w-[44px]) */}
            <div className="bg-shop-darkest p-0.5 rounded border border-shop-border flex items-center">
              <button
                type="button"
                onClick={() => setActiveMode('workspace')}
                className={`min-h-[44px] min-w-[44px] px-3 py-1.5 rounded text-xs font-mono font-bold uppercase tracking-wider transition-colors ${
                  activeMode === 'workspace'
                    ? 'bg-shop-panel text-shop-highlight border border-shop-border shadow-sm'
                    : 'text-shop-muted hover:text-shop-light'
                }`}
              >
                Underwriter Workspace
              </button>
              <button
                type="button"
                onClick={() => setActiveMode('preview')}
                className={`min-h-[44px] min-w-[44px] px-3 py-1.5 rounded text-xs font-mono font-bold uppercase tracking-wider transition-colors ${
                  activeMode === 'preview'
                    ? 'bg-safety-amber text-shop-darkest border border-safety-amberDark shadow-sm font-extrabold'
                    : 'text-shop-muted hover:text-shop-light'
                }`}
              >
                Print Preview
              </button>
            </div>

            {/* Export JSON Button (min-h-[44px], min-w-[44px]) */}
            <MechanicalButton
              variant="outline"
              size="sm"
              onClick={handleExportJson}
              icon={<Download className="w-4 h-4 text-blueprint-cyan" />}
            >
              Export JSON
            </MechanicalButton>

            {/* Print / PDF Export Button (min-h-[44px], min-w-[44px]) */}
            <MechanicalButton
              variant="primary"
              size="sm"
              onClick={handlePrint}
              icon={<Printer className="w-4 h-4" />}
            >
              Print / Save PDF
            </MechanicalButton>
          </div>
        }
      >
        {/* KPI Metric Summary Strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Invested Capital */}
          <div className="bg-shop-darkest border border-shop-border p-4 rounded space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-mono text-shop-muted uppercase block">
                TOTAL INVESTED CAPITAL
              </span>
              <DollarSign className="w-4 h-4 text-shop-muted" />
            </div>
            <div className="text-2xl font-bold font-mono text-shop-highlight">
              {formatCurrency(valuation.totalInvestedCapital)}
            </div>
            <p className="text-[11px] text-shop-muted">
              Documented parts, sublet services, &amp; acquisition
            </p>
          </div>

          {/* Suggested Agreed Value */}
          <div className="bg-shop-darkest border-2 border-safety-amber p-4 rounded space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-mono text-safety-amber uppercase block">
                SUGGESTED AGREED VALUE (+{valuation.appreciationBufferPct}%)
              </span>
              <Award className="w-4 h-4 text-safety-amber" />
            </div>
            <div className="text-2xl font-bold font-mono text-safety-amber">
              {formatCurrency(valuation.suggestedAgreedValue)}
            </div>
            <p className="text-[11px] text-shop-muted">
              Recommended policy replacement ceiling
            </p>
          </div>

          {/* Provenance Completeness Score */}
          <div className="bg-shop-darkest border border-shop-border p-4 rounded space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-mono text-blueprint-cyan uppercase block">
                PROVENANCE COMPLETENESS
              </span>
              <ShieldCheck className="w-4 h-4 text-blueprint-cyan" />
            </div>
            <div className="text-2xl font-bold font-mono text-blueprint-cyan">
              {provenanceScore.totalScore} / 100
            </div>
            <p className="text-[11px] font-bold text-safety-green">
              {scoreInfo.band}
            </p>
          </div>

          {/* Sourced/Installed BOM Parts */}
          <div className="bg-shop-darkest border border-shop-border p-4 rounded space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-mono text-safety-green uppercase block">
                SOURCED / INSTALLED BOM
              </span>
              <CheckCircle2 className="w-4 h-4 text-safety-green" />
            </div>
            <div className="text-2xl font-bold font-mono text-shop-highlight">
              {formatCurrency(valuation.totalBOMPartsCost)}
            </div>
            <p className="text-[11px] text-shop-muted">
              {bomItems.filter((i) => i.status !== 'Needed').length} verified component line items
            </p>
          </div>
        </div>
      </TactilePanel>

      {/* =====================================================================
          MODE A: INTERACTIVE UNDERWRITER WORKSPACE
          ===================================================================== */}
      {activeMode === 'workspace' && (
        <div className="space-y-6">
          {/* Underwriting Scorecard Breakdown & Progress Meters */}
          <TactilePanel
            title="Hagerty / Grundy Underwriting Provenance Audit"
            subtitle="Quantitative breakdown across 6 collector vehicle appraisal criteria (100 Point Scale)"
            badge={
              <span className="p-1.5 bg-shop-darkest rounded border border-shop-border text-blueprint-cyan">
                <Award className="w-4 h-4" />
              </span>
            }
            action={
              <span
                className="px-2.5 py-1 rounded text-xs font-mono font-bold uppercase tracking-wider border"
                style={{
                  color: scoreInfo.badgeColor,
                  backgroundColor: scoreInfo.badgeBg,
                  borderColor: scoreInfo.badgeBorder,
                }}
              >
                {scoreInfo.band} ({provenanceScore.totalScore} Pts)
              </span>
            }
          >
            <div className="space-y-6">
              {/* Score Meter Bars */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* 1. VIN Verification */}
                <div className="bg-shop-darkest border border-shop-border p-3 rounded space-y-2">
                  <div className="flex justify-between items-center text-xs font-mono">
                    <span className="text-shop-light font-bold">1. VIN &amp; Cowl Tag</span>
                    <span className="text-safety-amber font-bold">
                      {provenanceScore.vinVerified.earned} / {provenanceScore.vinVerified.max} pts
                    </span>
                  </div>
                  <div className="w-full bg-shop-panel h-2 rounded overflow-hidden">
                    <div
                      className="bg-safety-amber h-full transition-all duration-300"
                      style={{
                        width: `${(provenanceScore.vinVerified.earned / provenanceScore.vinVerified.max) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-shop-muted">
                    {project.vin ? `VIN: ${project.vin}` : 'Missing chassis VIN'} | Cowl tag decoded
                  </p>
                </div>

                {/* 2. 7-Phase Rebuild Coverage */}
                <div className="bg-shop-darkest border border-shop-border p-3 rounded space-y-2">
                  <div className="flex justify-between items-center text-xs font-mono">
                    <span className="text-shop-light font-bold">2. 7-Phase Coverage</span>
                    <span className="text-blueprint-cyan font-bold">
                      {provenanceScore.phaseCoverage.earned} / {provenanceScore.phaseCoverage.max} pts
                    </span>
                  </div>
                  <div className="w-full bg-shop-panel h-2 rounded overflow-hidden">
                    <div
                      className="bg-blueprint-cyan h-full transition-all duration-300"
                      style={{
                        width: `${(provenanceScore.phaseCoverage.earned / provenanceScore.phaseCoverage.max) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-shop-muted">
                    Documented progress across the 7 restoration lifecycle phases
                  </p>
                </div>

                {/* 3. Photo Evidence Plates */}
                <div className="bg-shop-darkest border border-shop-border p-3 rounded space-y-2">
                  <div className="flex justify-between items-center text-xs font-mono">
                    <span className="text-shop-light font-bold">3. Photo Evidence</span>
                    <span className="text-safety-green font-bold">
                      {provenanceScore.photoEvidence.earned} / {provenanceScore.photoEvidence.max} pts
                    </span>
                  </div>
                  <div className="w-full bg-shop-panel h-2 rounded overflow-hidden">
                    <div
                      className="bg-safety-green h-full transition-all duration-300"
                      style={{
                        width: `${(provenanceScore.photoEvidence.earned / provenanceScore.photoEvidence.max) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-shop-muted">
                    {photoLogs.filter((p) => p.includeInDossier).length} Curated plates &amp; before/after pairs
                  </p>
                </div>

                {/* 4. Receipt Audit Trail */}
                <div className="bg-shop-darkest border border-shop-border p-3 rounded space-y-2">
                  <div className="flex justify-between items-center text-xs font-mono">
                    <span className="text-shop-light font-bold">4. Receipt Audit Trail</span>
                    <span className="text-safety-amber font-bold">
                      {provenanceScore.receiptAuditTrail.earned} / {provenanceScore.receiptAuditTrail.max} pts
                    </span>
                  </div>
                  <div className="w-full bg-shop-panel h-2 rounded overflow-hidden">
                    <div
                      className="bg-safety-amber h-full transition-all duration-300"
                      style={{
                        width: `${(provenanceScore.receiptAuditTrail.earned / provenanceScore.receiptAuditTrail.max) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-shop-muted">
                    Percentage of purchased BOM parts with attached invoice references
                  </p>
                </div>

                {/* 5. Hardware Cataloging */}
                <div className="bg-shop-darkest border border-shop-border p-3 rounded space-y-2">
                  <div className="flex justify-between items-center text-xs font-mono">
                    <span className="text-shop-light font-bold">5. Hardware Bins &amp; Bags</span>
                    <span className="text-blueprint-cyan font-bold">
                      {provenanceScore.hardwareCataloging.earned} / {provenanceScore.hardwareCataloging.max} pts
                    </span>
                  </div>
                  <div className="w-full bg-shop-panel h-2 rounded overflow-hidden">
                    <div
                      className="bg-blueprint-cyan h-full transition-all duration-300"
                      style={{
                        width: `${(provenanceScore.hardwareCataloging.earned / provenanceScore.hardwareCataloging.max) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-shop-muted">
                    {subAssemblies.length} Sub-assemblies &amp; {hardwareBags.length} tagged fastener baggies
                  </p>
                </div>

                {/* 6. Sublet Invoices */}
                <div className="bg-shop-darkest border border-shop-border p-3 rounded space-y-2">
                  <div className="flex justify-between items-center text-xs font-mono">
                    <span className="text-shop-light font-bold">6. Sublet Documentation</span>
                    <span className="text-safety-green font-bold">
                      {provenanceScore.subletDocumentation.earned} / {provenanceScore.subletDocumentation.max} pts
                    </span>
                  </div>
                  <div className="w-full bg-shop-panel h-2 rounded overflow-hidden">
                    <div
                      className="bg-safety-green h-full transition-all duration-300"
                      style={{
                        width: `${(provenanceScore.subletDocumentation.earned / provenanceScore.subletDocumentation.max) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-shop-muted">
                    {subletExpenses.length} Documented specialist machining &amp; paint invoices
                  </p>
                </div>
              </div>

              {/* Assessment Advisory Banner */}
              <div className="bg-shop-panel border border-shop-border p-3.5 rounded flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-safety-amber shrink-0 mt-0.5" />
                <div className="space-y-1 text-xs">
                  <div className="font-bold text-shop-highlight font-mono uppercase">
                    Underwriting Approval Readiness: {scoreInfo.band}
                  </div>
                  <p className="text-shop-muted leading-relaxed">
                    {scoreInfo.description}
                  </p>
                </div>
              </div>
            </div>
          </TactilePanel>

          {/* Editable Underwriter Declaration Settings Panel */}
          <TactilePanel
            title="Appraisal Parameters &amp; Underwriting Declarations"
            subtitle="Configure insurer destination, replacement buffer percentage, labor rates, and garage security"
            badge={
              <span className="p-1.5 bg-shop-darkest rounded border border-shop-border text-safety-amber">
                <Sliders className="w-4 h-4" />
              </span>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 font-mono text-xs">
              {/* Intended Underwriter Dropdown */}
              <div className="space-y-1.5">
                <label className="text-shop-muted uppercase block text-[11px]">
                  Intended Insurance Carrier
                </label>
                <select
                  value={intendedUnderwriter}
                  onChange={(e) => setIntendedUnderwriter(e.target.value as any)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest text-shop-light border border-shop-border rounded focus:border-safety-amber focus:outline-none cursor-pointer"
                >
                  <option value="Hagerty">Hagerty Classic Car Insurance</option>
                  <option value="Grundy">Grundy Worldwide Collector Program</option>
                  <option value="American Collectors">American Collectors Insurance</option>
                  <option value="Leland-West">Leland-West Specialty Insurance</option>
                  <option value="Auction Record">Auction Catalog Provenance Record</option>
                  <option value="General">General Collector Vehicle Appraisal</option>
                </select>
              </div>

              {/* Appreciation / Replacement Buffer Slider */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-[11px]">
                  <label className="text-shop-muted uppercase">Replacement Buffer</label>
                  <span className="text-safety-amber font-bold">+{appreciationBufferPct}%</span>
                </div>
                <div className="flex items-center gap-3 min-h-[44px]">
                  <input
                    type="range"
                    min="0"
                    max="30"
                    step="1"
                    value={appreciationBufferPct}
                    onChange={(e) => setAppreciationBufferPct(Number(e.target.value))}
                    className="w-full accent-safety-amber cursor-pointer"
                    aria-label="Appreciation buffer percentage slider"
                  />
                  <span className="w-12 text-right font-bold text-shop-highlight">
                    {appreciationBufferPct}%
                  </span>
                </div>
              </div>

              {/* Hourly Labor Rate */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-[11px]">
                  <label className="text-shop-muted uppercase">Certified Labor Valuation</label>
                  <span className="text-safety-green font-bold">${customHourlyLaborRate}/hr</span>
                </div>
                <div className="flex items-center gap-3 min-h-[44px]">
                  <input
                    type="range"
                    min="50"
                    max="175"
                    step="5"
                    value={customHourlyLaborRate}
                    onChange={(e) => setCustomHourlyLaborRate(Number(e.target.value))}
                    className="w-full accent-safety-green cursor-pointer"
                    aria-label="Hourly labor rate slider"
                  />
                  <span className="w-12 text-right font-bold text-shop-highlight">
                    ${customHourlyLaborRate}
                  </span>
                </div>
              </div>

              {/* Storage Facility Type */}
              <div className="space-y-1.5">
                <label className="text-shop-muted uppercase block text-[11px]">
                  Declared Storage Facility
                </label>
                <select
                  value={storageFacilityType}
                  onChange={(e) => setStorageFacilityType(e.target.value as any)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest text-shop-light border border-shop-border rounded focus:border-safety-amber focus:outline-none cursor-pointer"
                >
                  <option value="Climate-Controlled Garage">Climate-Controlled Dedicated Garage</option>
                  <option value="Locked Residential Garage">Locked Residential Garage</option>
                  <option value="Commercial Facility">Commercial Storage Facility</option>
                </select>
              </div>

              {/* Annual Mileage Estimate */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-[11px]">
                  <label className="text-shop-muted uppercase">Annual Pleasure Mileage</label>
                  <span className="text-blueprint-cyan font-bold">{annualEstimatedMileage} Mi/Yr</span>
                </div>
                <div className="flex items-center gap-3 min-h-[44px]">
                  <input
                    type="range"
                    min="500"
                    max="7500"
                    step="500"
                    value={annualEstimatedMileage}
                    onChange={(e) => setAnnualEstimatedMileage(Number(e.target.value))}
                    className="w-full accent-blueprint-cyan cursor-pointer"
                    aria-label="Annual mileage slider"
                  />
                  <span className="w-14 text-right font-bold text-shop-highlight">
                    {annualEstimatedMileage}
                  </span>
                </div>
              </div>

              {/* Prepared By Restorer Field */}
              <div className="space-y-1.5">
                <label className="text-shop-muted uppercase block text-[11px]">
                  Dossier Prepared By
                </label>
                <input
                  type="text"
                  value={preparedBy}
                  onChange={(e) => setPreparedBy(e.target.value)}
                  placeholder="Restoration Shop / Lead Builder"
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest text-shop-light border border-shop-border rounded focus:border-safety-amber focus:outline-none"
                />
              </div>

              {/* Owner Signature Date */}
              <div className="space-y-1.5">
                <label className="text-shop-muted uppercase block text-[11px]">
                  Owner Policyholder Sign-off Date
                </label>
                <input
                  type="date"
                  value={ownerSignatureDate}
                  onChange={(e) => setOwnerSignatureDate(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest text-shop-light border border-shop-border rounded focus:border-safety-amber focus:outline-none"
                />
              </div>
            </div>
          </TactilePanel>

          {/* Capital Investment Ledger */}
          <TactilePanel
            title="Documented Capital Investment Ledger"
            subtitle="Itemized breakdown of vehicle acquisition, BOM replacement parts by condition, and sublet machining"
            badge={
              <span className="p-1.5 bg-shop-darkest rounded border border-shop-border text-safety-green">
                <FileText className="w-4 h-4" />
              </span>
            }
          >
            <DossierSummaryTable
              valuation={valuation}
              bomItems={bomItems}
              subletExpenses={subletExpenses}
              laborRecords={adjustedLaborRecords}
            />
          </TactilePanel>

          {/* Photographic Evidence Gallery */}
          <TactilePanel
            title="Curated Photographic Rebuild Plates"
            subtitle="Before-and-after restoration pairs and milestone verification photos for underwriting appraisal"
            badge={
              <span className="p-1.5 bg-shop-darkest rounded border border-shop-border text-safety-amber">
                <Camera className="w-4 h-4" />
              </span>
            }
          >
            <DossierPhotoGrid photos={photoLogs} filterCuratedOnly={true} />
          </TactilePanel>
        </div>
      )}

      {/* =====================================================================
          MODE B: FORMAL PRINT DOCUMENT PREVIEW
          ===================================================================== */}
      {activeMode === 'preview' && (
        <div className="space-y-4">
          <div className="bg-shop-dark border border-safety-amber/40 p-3.5 rounded flex items-center justify-between text-xs font-mono">
            <span className="text-safety-amber font-bold">
              PRINT DOCUMENT PREVIEW MODE ACTIVE
            </span>
            <span className="text-shop-muted">
              Click &quot;Print / Save PDF&quot; above to generate hardcopy or insurance PDF
            </span>
          </div>

          <ValuationDossier
            dossier={dossierPayload}
            onPrint={handlePrint}
            onExportJson={handleExportJson}
            isPrintMode={true}
          />
        </div>
      )}
    </div>
  );
};
